<script lang="ts">
  import { onMount } from 'svelte';
  import type { ApiClient } from '../lib/api';
  import { formatDate } from '../lib/format';
  import Loading from '../lib/Loading.svelte';
  import type { SuppressionAudit, SuppressionRule } from '../lib/types';

  let { api, canManage }: { api: ApiClient; canManage: boolean } = $props();
  let rules = $state<SuppressionRule[]>([]);
  let audit = $state<SuppressionAudit[]>([]);
  let ruleCursor = $state<string | null>(null);
  let auditCursor = $state<string | null>(null);
  let loading = $state(true);
  let error = $state('');
  let deleting = $state('');

  async function load() {
    try {
      const [ruleResult, auditResult] = await Promise.all([api.suppressionRules(), api.suppressionAudit()]);
      rules = ruleResult.items;
      audit = auditResult.items;
      ruleCursor = ruleResult.nextCursor;
      auditCursor = auditResult.nextCursor;
      error = '';
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'Suppression rules could not be loaded.';
    } finally {
      loading = false;
    }
  }

  async function loadMoreRules() {
    if (!ruleCursor) return;
    try {
      const result = await api.suppressionRules(ruleCursor);
      rules = [...rules, ...result.items];
      ruleCursor = result.nextCursor;
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'More suppression rules could not be loaded.';
    }
  }

  async function loadMoreAudit() {
    if (!auditCursor) return;
    try {
      const result = await api.suppressionAudit(auditCursor);
      audit = [...audit, ...result.items];
      auditCursor = result.nextCursor;
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'More audit events could not be loaded.';
    }
  }

  async function remove(rule: SuppressionRule) {
    if (!window.confirm(`Delete the suppression for ${rule.criteria.findingType}? Retained findings will become visible again.`)) return;
    deleting = rule.id;
    try {
      await api.deleteSuppression(rule.id);
      await load();
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'The suppression rule could not be deleted.';
    } finally {
      deleting = '';
    }
  }

  function scopeLabel(rule: SuppressionRule): string {
    return rule.criteria.scope === 'global' ? 'All customers' : `Customer ${rule.criteria.customerId}`;
  }

  onMount(() => load());
</script>

<section class="page-heading"><div><p class="eyebrow">GuardDuty controls</p><h1>Suppression rules</h1><p>Current global and customer-specific finding suppressions, with an immutable change history.</p></div></section>
{#if error}<p class="inline-error" role="alert">{error}</p>{/if}
{#if loading}<Loading />{:else}
  <section class="detail-section"><div class="section-heading"><h2>Active rules</h2><span>{rules.length}</span></div>
    {#if rules.length === 0}<p class="muted">No GuardDuty findings are currently suppressed.</p>{:else}<div class="suppression-list">{#each rules as rule}<article><div><strong>{rule.criteria.findingType}</strong><span>{scopeLabel(rule)}</span><small>{rule.reason}</small><code>{rule.id}</code></div><dl><div><dt>Qualifiers</dt><dd>{rule.criteria.severityBand ?? 'Any severity'} · {rule.criteria.sourceAccountId ?? 'Any account'} · {rule.criteria.resource ? `${rule.criteria.resource.type} ${rule.criteria.resource.id}` : 'Any resource'}</dd></div><div><dt>Created</dt><dd>{formatDate(rule.createdAt)} by {rule.createdBy.username ?? rule.createdBy.subject}</dd></div></dl>{#if canManage}<button class="secondary danger-button" disabled={deleting === rule.id} onclick={() => remove(rule)}>{deleting === rule.id ? 'Deleting…' : 'Delete rule'}</button>{/if}</article>{/each}</div>{#if ruleCursor}<button class="secondary load-more" onclick={loadMoreRules}>Load more rules</button>{/if}{/if}
  </section>
  <section class="detail-section"><div class="section-heading"><h2>Audit history</h2><span>{audit.length}</span></div>
    {#if audit.length === 0}<p class="muted">No suppression changes have been recorded.</p>{:else}<div class="audit-list">{#each audit as event}<article><strong>{event.action === 'created' ? 'Created' : 'Deleted'} · {event.rule.criteria.findingType}</strong><span>{formatDate(event.occurredAt)} by {event.actor.username ?? event.actor.subject}</span><small>{event.rule.reason}</small></article>{/each}</div>{#if auditCursor}<button class="secondary load-more" onclick={loadMoreAudit}>Load more audit events</button>{/if}{/if}
  </section>
{/if}
